import { getClientLogExport, noteClientLog } from "./clientLogBuffer";
import { perfDiagnosticsLines } from "./perfProbe";
import {
  getDiagActionsExport,
  getDiagFlashExport,
  getDiagOpsExport,
  getDiagSceneSnapshotExport,
} from "./boardDiagTrail";

const MAX_MESSAGE_CHARS = 12_000;
const MAX_LOG_IN_MESSAGE = 2_000;

export type BoardBugReportKind = "problem" | "idea";

export type BoardBugReportContext = {
  canEdit?: boolean;
  wsState?: string;
  accountKind?: string | null;
  planCode?: string | null;
  tool?: string | null;
  /** owner | share | guest | view_only */
  accessMode?: string | null;
  sessionAccess?: boolean;
  hasShareToken?: boolean;
};

function readEditorBuild(): string | undefined {
  try {
    const w = window as Window & { __BEZPART_EDITOR_BUILD__?: string };
    if (typeof w.__BEZPART_EDITOR_BUILD__ === "string" && w.__BEZPART_EDITOR_BUILD__) {
      return w.__BEZPART_EDITOR_BUILD__;
    }
  } catch {
    /* ignore */
  }
  return undefined;
}

function readNetworkInfo(): { effectiveType?: string; rtt?: number } | undefined {
  const conn = (
    navigator as Navigator & {
      connection?: { effectiveType?: string; rtt?: number };
    }
  ).connection;
  if (!conn) return undefined;
  return { effectiveType: conn.effectiveType, rtt: conn.rtt };
}

function formatDiagnosticsBlock(
  boardId: string,
  boardUrl: string,
  ctx?: BoardBugReportContext
): string {
  const buildId = readEditorBuild();
  const network = readNetworkInfo();
  const clientLog = getClientLogExport();
  const logTail =
    clientLog.length > MAX_LOG_IN_MESSAGE
      ? `…(обрезано)\n${clientLog.slice(-MAX_LOG_IN_MESSAGE)}`
      : clientLog;

  const lines: string[] = [];
  lines.push(`board_id: ${boardId}`);
  lines.push(`board_url: ${boardUrl}`);
  if (buildId) lines.push(`editor_build: ${buildId}`);
  lines.push(`timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`);
  lines.push(`locale: ${navigator.language}`);
  lines.push(`online: ${navigator.onLine ? "yes" : "no"}`);
  if (network?.effectiveType) {
    lines.push(`network: ${network.effectiveType}, rtt ${network.rtt ?? "?"}ms`);
  }
  if (ctx?.canEdit !== undefined) lines.push(`can_edit: ${ctx.canEdit ? "yes" : "no"}`);
  if (ctx?.accessMode) lines.push(`access_mode: ${ctx.accessMode}`);
  if (ctx?.sessionAccess !== undefined) {
    lines.push(`session_access: ${ctx.sessionAccess ? "yes" : "no"}`);
  }
  if (ctx?.hasShareToken !== undefined) {
    lines.push(`share_token: ${ctx.hasShareToken ? "yes" : "no"}`);
  }
  if (ctx?.wsState) lines.push(`ws: ${ctx.wsState}`);
  if (ctx?.accountKind) lines.push(`account: ${ctx.accountKind}`);
  if (ctx?.planCode) lines.push(`plan: ${ctx.planCode}`);
  if (ctx?.tool) lines.push(`tool: ${ctx.tool}`);
  // Скорость: видеокарта, ядра, перерисовка, подвисания — у кого и почему тормозит.
  lines.push("", "perf:", ...perfDiagnosticsLines());

  lines.push("", "scene:", getDiagSceneSnapshotExport());
  lines.push("", "recent_ops:", getDiagOpsExport());
  lines.push("", "flash:", getDiagFlashExport());
  lines.push("", "actions:", getDiagActionsExport());

  if (logTail && logTail !== "—") {
    lines.push("", "client_log:", logTail);
  } else {
    lines.push("", "client_log: (пусто — ошибок в сессии не было)");
  }

  return lines.join("\n");
}

function reportKindLabel(kind: BoardBugReportKind): string {
  return kind === "idea" ? "идея" : "проблема";
}

/** Поля для API/Formspree — диагностика и логи внутри message. */
export function buildBoardBugReportPayload(input: {
  userMessage: string;
  reporterName: string;
  boardId?: string;
  reportKind?: BoardBugReportKind;
  context?: BoardBugReportContext;
}): Record<string, string> {
  noteClientLog("[bug-report]", "submit");
  const reporterName = input.reporterName.trim() || "Без имени";
  const boardId = input.boardId?.trim() || "—";
  const boardUrl = window.location.href;
  const reportKind = input.reportKind ?? "problem";
  const diagnostics = formatDiagnosticsBlock(boardId, boardUrl, input.context);
  let message = `${input.userMessage.trim()}\n\n---\nДиагностика:\n${diagnostics}`;
  if (message.length > MAX_MESSAGE_CHARS) {
    message = `${message.slice(0, MAX_MESSAGE_CHARS - 20)}\n…(обрезано)`;
  }

  const buildId = readEditorBuild();
  const summaryParts: string[] = [reportKindLabel(reportKind)];
  if (buildId) summaryParts.push(`build ${buildId}`);
  if (input.context?.wsState) summaryParts.push(`ws ${input.context.wsState}`);
  if (input.context?.planCode) summaryParts.push(`plan ${input.context.planCode}`);
  if (input.context?.accessMode) summaryParts.push(input.context.accessMode);
  if (input.context?.canEdit === false) summaryParts.push("view-only");

  const kindTag = reportKind === "idea" ? "идея" : "проблема";

  return {
    message,
    name: reporterName,
    board_id: boardId,
    board_url: boardUrl,
    room_id: boardId,
    page_url: boardUrl,
    report_kind: kindTag,
    user_agent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    tech_summary: summaryParts.join(" · ") || "board",
    product: "board",
    _subject: `[Доска · ${kindTag}] ${reporterName}${boardId !== "—" ? ` · ${boardId}` : ""}`,
  };
}
